import React, { createContext, useContext, useState, ReactNode, useEffect, useRef, useCallback } from "react";
import {
  Item,
  ItemSnapshot,
  ItemStatus,
  Order,
  OrderSnapshot,
  Shipment,
} from "../types/DB2";
import {
  collection,
  doc,
  getDocs,
  GeoPoint,
  query,
  Timestamp,
  updateDoc,
  where,
  onSnapshot, // <-- Add this import
} from "firebase/firestore";
import { arrayUnion } from "firebase/firestore";
import { auth, db } from "../firebaseConfig";
import { useUserData } from "./UserDataFetcher";
import { useAuthUser } from "./UserManager";
import { useOfflineSync } from "./OfflineSyncProvider";
import { ItemStatusMutationPayload, OfflineProcessorResult, QueueDropoffPayload } from "../offline";
import config from "../config"; 
import {
  normalizeShipmentSnapshot,
  normalizeOrderItemsSnapshot,
} from "../utils/normalizeSnapshots";
import { logClientSyncError, type ClientSyncErrorPayload } from "../services/logClientSyncError";

const dbPrefix = config.productionDbPrefix;

const normalizeDestination = (value: string | undefined | null) => {
  if (typeof value !== "string") return "";
  const trimmed = value.trim();
  return trimmed.length ? trimmed : "";
};

const computeDestinationsAggregate = (
  items: Record<string, ItemSnapshot | undefined> | undefined | null
) => {
  const names = new Map<string, string>();
  const geo: Record<string, GeoPoint> = {};
  if (!items) {
    return { destinations: [] as string[], destinationsGeo: {} as Record<string, GeoPoint> };
  }
  Object.values(items).forEach((item) => {
    if (!item) return;
    const normalized = normalizeDestination(item.destination);
    if (!normalized) return;
    const key = normalized.toLowerCase();
    if (!names.has(key)) {
      names.set(key, normalized);
    }
    if (item.dropoffGeo) {
      geo[normalized] = item.dropoffGeo as unknown as GeoPoint;
    }
  });
  return {
    destinations: Array.from(names.values()),
    destinationsGeo: geo,
  };
};

const collectShipmentItems = (
  ordersSnapshot: Record<string, OrderSnapshot> | undefined
): Record<string, ItemSnapshot> => {
  const items: Record<string, ItemSnapshot> = {};
  if (!ordersSnapshot) return items;
  Object.values(ordersSnapshot).forEach((orderSnapshot) => {
    if (!orderSnapshot?.includedItemsSnapshot) return;
    Object.entries(orderSnapshot.includedItemsSnapshot).forEach(
      ([id, snapshot]) => {
        if (snapshot) {
          items[id] = snapshot;
        }
      }
    );
  });
  return items;
};

const computeShipmentHashSeed = (raw: Shipment): string => {
  try {
    const seed = {
      updatedAt: (raw as any)?.updatedAt ?? null,
      status: raw.status ?? null,
      dealerIds: raw.dealerIds ?? null,
      ordersSnapshot: raw.ordersSnapshot ?? null,
    };
    return JSON.stringify(seed);
  } catch (error) {
    console.warn("[PrimaryFetchedData] computeShipmentHashSeed failed", error);
    return `${Date.now()}|${Math.random()}`;
  }
};

export type DropoffLocationPayload = QueueDropoffPayload;

export type ResolvedDropoffUpdate = {
  geoPoint: GeoPoint;
  accuracyMeters?: number;
  source?: string;
  capturedAt?: Timestamp;
};

export type BatchUpdateArg = {
  itemId: string;
  newStatus: ItemStatus;
  newProcessedState?: boolean | undefined;
  dropoff?: ResolvedDropoffUpdate | undefined;
  updateMethod?: "manual" | "scan";
};
export type DBUpdateObject = {
  itemId: string;
  newStatus: ItemStatus;
  newProcessedState?: boolean | undefined;
  dropoff?: ResolvedDropoffUpdate | undefined;
  updateMethod?: "manual" | "scan";
  scanHistoryEntry?: Record<string, unknown> | undefined;
};
export type UpdateObject = {
  itemId: string;
  orderId: string;
  newStatus: ItemStatus;
  shipmentIndex: number;
  newProcessedState?: boolean | undefined;
  dropoff?: DropoffLocationPayload | undefined;
  dropoffResolved?: ResolvedDropoffUpdate | undefined;
  updateMethod?: "manual" | "scan";
  prevStatus?: ItemStatus | undefined;
};

export type UpdateDbResult =
  | { ok: true }
  | {
      ok: false;
      reason: 'unauthorized' | 'network' | 'unknown';
      failedItems: string[];
      error: unknown;
    };

type SyncErrorInput = Omit<
  ClientSyncErrorPayload,
  | "uid"
  | "email"
  | "role"
  | "queueSize"
  | "queuePaused"
  | "pauseReason"
  | "isOnline"
  | "isForcedOffline"
>;

interface PrimaryFetchedDataContextType {
  updateItemStatus: (update: UpdateObject) => Promise<boolean>;
  updateItemStatuses: (updates: UpdateObject[]) => Promise<boolean>;
  shipments: Shipment[];
  orders: Order[];
  fetchShipmentsByManufacturerId: () => void;
  fetchShipmentsForDriver: () => void;
  fetchOrdersByDealerId: () => void;
}

const PrimaryFetchedDataContext = createContext<PrimaryFetchedDataContextType>({
  fetchShipmentsByManufacturerId: () => {},
  fetchShipmentsForDriver: () => {},
  fetchOrdersByDealerId: () => {},
  updateItemStatus: () => Promise.resolve(false),
  shipments: [],
  orders: [],
  updateItemStatuses: () => Promise.resolve(false),
});

export const usePrimaryFetchedData = () => {
  const context = useContext(PrimaryFetchedDataContext);
  if (context === undefined) {
    throw new Error("PrimaryFetchedDataContext creation failed");
  }
  return context;
};

export const PrimaryFetchedDataProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const shipmentsRef = useRef<Shipment[]>([]);
  const ordersRef = useRef<Order[]>([]);
  const cacheHydratedRef = useRef(false);
  const dealerShipmentsUnsubRef = useRef<(() => void) | null>(null);
  const dealerShipmentMatchesRef = useRef<Map<string, Shipment>>(new Map());
  const dealerShipmentHashesRef = useRef<Map<string, string>>(new Map());
  useEffect(() => {
    shipmentsRef.current = shipments;
  }, [shipments]);

  useEffect(() => {
    ordersRef.current = orders;
  }, [orders]);

  const userData = useUserData();
  const { authUser } = useAuthUser();
  const {
    featureEnabled,
    isOnline,
    isForcedOffline,
    enqueueItemStatus,
    registerProcessor,
    queueManager,
    cacheManager,
    queueState,
    resumeSync,
  } = useOfflineSync();

  const cacheWriteCleanupRef = useRef<(() => void) | null>(null);
  const lastAuthResumeKeyRef = useRef<string | null>(null);
  const logSyncIssue = useCallback(
    (payload: SyncErrorInput) => {
      void logClientSyncError({
        ...payload,
        uid: authUser?.uid,
        email: authUser?.email ?? undefined,
        role: userData?.role,
        isOnline,
        isForcedOffline,
        queueSize: queueState?.items.length ?? 0,
        queuePaused: queueState?.paused ?? false,
        pauseReason: queueState?.pausedReason,
      });
    },
    [
      authUser?.email,
      authUser?.uid,
      isForcedOffline,
      isOnline,
      queueState?.items.length,
      queueState?.paused,
      queueState?.pausedReason,
      userData?.role,
    ]
  );

  useEffect(() => {
    if (!featureEnabled) return;
    const currentQueueState = queueState;
    if (!currentQueueState?.paused) return;
    if (!authUser) return;
    if (!isOnline || isForcedOffline) return;
    const reason = currentQueueState.pausedReason?.toLowerCase() ?? "";
    const isAuthPause = reason.includes("auth");
    if (!isAuthPause) return;
    const key = `${currentQueueState.pausedReason ?? ""}|${currentQueueState.items
      .map((entry) => entry.id)
      .join(",")}`;
    if (lastAuthResumeKeyRef.current === key) {
      return;
    }
    lastAuthResumeKeyRef.current = key;
    console.log(
      "[PrimaryFetchedData] Auth pause detected with active user; attempting resume",
      {
        userId: authUser.uid,
        pausedReason: currentQueueState.pausedReason,
      }
    );
    void resumeSync();
  }, [
    authUser,
    featureEnabled,
    isForcedOffline,
    isOnline,
    queueState,
    resumeSync,
  ]);

  useEffect(() => {
    if (!featureEnabled || !cacheManager) return;
    if (!shipments.length && !orders.length) return;

    if (cacheWriteCleanupRef.current) {
      cacheWriteCleanupRef.current();
      cacheWriteCleanupRef.current = null;
    }

    const schedule = (cb: () => void): (() => void) => {
      if (typeof window === "undefined") {
        cb();
        return () => {};
      }
      const win = window as any;
      if (typeof win.requestIdleCallback === "function") {
        const id = win.requestIdleCallback(cb, { timeout: 500 });
        return () => {
          if (typeof win.cancelIdleCallback === "function") {
            win.cancelIdleCallback(id);
          }
        };
      }
      const id = window.setTimeout(cb, 250);
      return () => window.clearTimeout(id);
    };

    const cancel = schedule(() => {
      void cacheManager
        .write({
          shipments: shipmentsRef.current,
          orders: ordersRef.current,
          lastUpdatedAt: Date.now(),
        })
        .catch((error) => {
          console.warn("[PrimaryFetchedData] failed to persist offline cache", error);
        });
      cacheWriteCleanupRef.current = null;
    });
    cacheWriteCleanupRef.current = cancel;

    return () => {
      cancel();
      if (cacheWriteCleanupRef.current === cancel) {
        cacheWriteCleanupRef.current = null;
      }
    };
  }, [cacheManager, featureEnabled, shipments, orders, isForcedOffline, isOnline]);

  useEffect(() => {
    if (!featureEnabled || !cacheManager) return;
    if (cacheHydratedRef.current) return;
    if (isOnline && !isForcedOffline) return;
    cacheManager
      .read()
      .then((snapshot) => {
        if (snapshot.shipments?.length) {
          setShipments(snapshot.shipments);
        }
        if (snapshot.orders?.length) {
          setOrders(snapshot.orders);
        }
        cacheHydratedRef.current = true;
      })
      .catch((error) => {
        console.warn("[PrimaryFetchedData] failed to hydrate cache", error);
      });
  }, [cacheManager, featureEnabled, isForcedOffline, isOnline]);

  useEffect(() => {
    if (isOnline && !isForcedOffline) {
      cacheHydratedRef.current = false;
    }
  }, [isForcedOffline, isOnline]);

  useEffect(() => {
    if (!userData) return;
    if (!userData.role) return;
    if (userData.role === "manufacturer") {
      fetchShipmentsByManufacturerId();
    } else if (userData.role === "dealer") {
      fetchOrdersByDealerId();
      fetchShipmentsByDealerId();
    } else if (userData.role === "driver") {
      fetchShipmentsForDriver();
    }


  }, [userData, authUser?.email, authUser?.uid]);


  const fetchOrdersByDealerId = () => {
    if (!userData) return;
    if (userData.role !== "dealer") return;
    if (typeof userData.dealerId === "undefined") return;
    (async () => {
      const ordersQuerySnapshot = query(
        collection(db, `${dbPrefix}orders`),
        where("dealerId", "==", userData?.dealerId)
      );
      if (!ordersQuerySnapshot) return;
      const ordersCollection = await getDocs(ordersQuerySnapshot);
      if (!ordersCollection) return;
      const fetchedOrders = ordersCollection.docs.map((doc) => {
        const data = doc.data() as Order;
        const orderId = data.id || doc.id;
        const normalizedItems = data.itemsSnapshot
          ? normalizeOrderItemsSnapshot(orderId, data.itemsSnapshot, "PrimaryFetchedData:dealerOrders")
          : undefined;
        return {
          ...data,
          id: orderId,
          ...(normalizedItems ? { itemsSnapshot: normalizedItems } : {}),
        };
      });
      setOrders(fetchedOrders);
      console.debug("[PrimaryFetchedData] fetchOrdersByDealerId result", {
        dealerId: userData.dealerId,
        count: fetchedOrders.length,
      });
    })();
  };

  const fetchShipmentsByDealerId = () => {
    if (!userData) {
      dealerShipmentsUnsubRef.current?.();
      dealerShipmentsUnsubRef.current = null;
      return;
    }
    if (userData.role !== "dealer") {
      dealerShipmentsUnsubRef.current?.();
      dealerShipmentsUnsubRef.current = null;
      return;
    }
    if (typeof userData.dealerId === "undefined") {
      dealerShipmentsUnsubRef.current?.();
      dealerShipmentsUnsubRef.current = null;
      return;
    }

    dealerShipmentsUnsubRef.current?.();
    dealerShipmentsUnsubRef.current = null;

    const dealerId = userData.dealerId;
    const shipmentsRefCollection = collection(db, `${dbPrefix}shipments`);

    const dealerIdsQuery = query(
      shipmentsRefCollection,
      where("dealerIds", "array-contains", dealerId)
    );

    const unsubscribeFromDealerIds = onSnapshot(dealerIdsQuery, (snapshot) => {
      const snapshotLabel = `[DealerSnapshot] size:${snapshot.size}`;
      if (typeof console !== "undefined" && typeof console.time === "function") {
        console.time(snapshotLabel);
      }
      const previousMatches = dealerShipmentMatchesRef.current;
      const previousHashes = dealerShipmentHashesRef.current;
      const nextMatches = new Map<string, Shipment>();
      const nextHashes = new Map<string, string>();
      let changed = false;

      snapshot.forEach((docSnapshot) => {
        const raw = docSnapshot.data() as Shipment;
        const shipmentId = raw.id || docSnapshot.id;
        const hash = computeShipmentHashSeed(raw);
        const prevHash = previousHashes.get(shipmentId);
        if (prevHash === hash) {
          const existing = previousMatches.get(shipmentId);
          if (existing) {
            nextMatches.set(shipmentId, existing);
            nextHashes.set(shipmentId, prevHash);
            return;
          }
        }
        const normalized = normalizeShipmentSnapshot(
          {
            ...raw,
            id: shipmentId,
          },
          "PrimaryFetchedData:dealer[array]"
        );
        nextMatches.set(shipmentId, normalized);
        nextHashes.set(shipmentId, hash);
        if (prevHash !== hash || !previousMatches.has(shipmentId)) {
          changed = true;
        }
      });

      // Detect removals
      for (const id of previousMatches.keys()) {
        if (!nextMatches.has(id)) {
          changed = true;
          break;
        }
      }

      // Detect order changes
      if (!changed) {
        const prevKeys = Array.from(previousMatches.keys());
        const nextKeys = Array.from(nextMatches.keys());
        if (prevKeys.length !== nextKeys.length) {
          changed = true;
        } else {
          for (let i = 0; i < prevKeys.length; i += 1) {
            if (prevKeys[i] !== nextKeys[i]) {
              changed = true;
              break;
            }
          }
        }
      }

      if (!changed) {
        if (typeof console !== "undefined" && typeof console.timeEnd === "function") {
          console.timeEnd(snapshotLabel);
        }
        return;
      }

      dealerShipmentMatchesRef.current = nextMatches;
      dealerShipmentHashesRef.current = nextHashes;
      const shipmentsForDealer = Array.from(nextMatches.values());
      setShipments((prev) => {
        if (
          prev.length === shipmentsForDealer.length &&
          prev.every((shipment, index) => shipment === shipmentsForDealer[index])
        ) {
          if (typeof console !== "undefined" && typeof console.timeEnd === "function") {
            console.timeEnd(snapshotLabel);
          }
          return prev;
        }
        console.debug("[PrimaryFetchedData] fetchShipmentsByDealerId result", {
          dealerId,
          source: "dealerIds",
          count: shipmentsForDealer.length,
        });
        if (typeof console !== "undefined" && typeof console.timeEnd === "function") {
          console.timeEnd(snapshotLabel);
        }
        return shipmentsForDealer;
      });
    });

    dealerShipmentsUnsubRef.current = () => {
      unsubscribeFromDealerIds();
      dealerShipmentMatchesRef.current = new Map();
      dealerShipmentHashesRef.current = new Map();
    };
  };

  const fetchShipmentsByManufacturerId = () => {
    if (!userData) return;
    if (userData.role !== "manufacturer") return;
    if (typeof userData.manufacturerId === "undefined") return;

    // Clean up previous listener if needed (optional, for advanced usage)
    // Store unsubscribe function in a ref if you want to support this

    const shipmentsQuery = query(
      collection(db, `${dbPrefix}shipments`),
      where("manufacturerId", "==", userData?.manufacturerId)
    );

    // Listen for real-time updates
    onSnapshot(shipmentsQuery, (snapshot) => {
      const fetchedShipments = snapshot.docs.map((doc) => {
        const raw = doc.data() as Shipment;
        const shipmentId = raw.id || doc.id;
        return normalizeShipmentSnapshot(
          {
            ...raw,
            id: shipmentId,
          },
          "PrimaryFetchedData:manufacturer"
        );
      });
      setShipments(fetchedShipments);
    });
  };

  useEffect(() => {
    return () => {
      dealerShipmentsUnsubRef.current?.();
      dealerShipmentsUnsubRef.current = null;
    };
  }, []);

  const fetchShipmentsForDriver = () => {
    if (!userData) return;
    if (userData.role !== "driver") return;


    const driverEmail = authUser?.email ?? undefined;
    const driverUid = authUser?.uid;
    const manufacturerId = userData.manufacturerId;

    if (!driverEmail && !driverUid) return;
    if (!manufacturerId) return;

    const driverShipmentsQuery = query(
      collection(db, `${dbPrefix}shipments`),
      where("manufacturerId", "==", manufacturerId)
    );

    onSnapshot(driverShipmentsQuery, (snapshot) => {
      const identifiers = new Set(
        [driverUid, driverEmail, driverEmail?.toLowerCase()].filter(
          (identifier): identifier is string => !!identifier
        )
      );

      const fetchedShipments = snapshot.docs
        .map((doc) => {
          const raw = doc.data() as Shipment;
          const shipmentId = raw.id || doc.id;
          return normalizeShipmentSnapshot(
            {
              ...raw,
              id: shipmentId,
            },
            "PrimaryFetchedData:driver"
          );
        })
        .filter((shipment) => {
          const shipmentDrivers = shipment.drivers;
          if (!shipmentDrivers || shipmentDrivers.length === 0) {
            return false;
          }
          return shipmentDrivers.some((driver) => {
            const normalized = driver.toLowerCase();
            return identifiers.has(driver) || identifiers.has(normalized);
          });
        });


      setShipments(fetchedShipments);
    });
  };

const updateItemStatusesDb = async (items: DBUpdateObject[]): Promise<UpdateDbResult> => {
    if (!items || items.length === 0) return { ok: true };

    const failures: { itemId: string; error: unknown }[] = [];

    for (const it of items) {
      if (!it.itemId || !it.newStatus) continue;
      const sanitized = it.itemId.replace(/\s/g, "");
      const itemDocRef = doc(db, `${dbPrefix}items`, sanitized);
      const updateObject: Partial<Item> & Record<string, unknown> = {
        status: it.newStatus,
      };
      // Set backend-only flag indicating manual vs scan update when provided
      if (typeof it.updateMethod === "string") {
        updateObject.manualUpdate = it.updateMethod === "manual";
        updateObject.lastUpdateMethod = it.updateMethod;
      }
      if (typeof it.newProcessedState !== "undefined") {
        updateObject.currentProcessed = it.newProcessedState;
      }
      if (it.dropoff) {
        updateObject.dropoffGeo = it.dropoff.geoPoint;
        if (typeof it.dropoff.accuracyMeters === "number") {
          updateObject.dropoffGeoAccuracyMeters = it.dropoff.accuracyMeters;
        }
        if (it.dropoff.source) {
          updateObject.dropoffGeoSource = it.dropoff.source;
        }
        if (it.dropoff.capturedAt) {
          updateObject.dropoffGeoCapturedAt = it.dropoff.capturedAt;
        }
      }

      // Append a scan history entry if provided so audit includes method
      if (it.scanHistoryEntry) {
        (updateObject as any).scanHistory = arrayUnion(it.scanHistoryEntry);
      }

      try {
        await updateDoc(itemDocRef, updateObject);
        if (it.dropoff) {
          console.debug("[updateItemStatusesDb] stored dropoff location", {
            itemId: sanitized,
            source: it.dropoff.source,
            accuracyMeters: it.dropoff.accuracyMeters,
          });
        }
      } catch (error) {
        const errorInfo =
          error && typeof error === "object"
            ? {
                name: (error as Error).name,
                message: (error as Error).message,
                code: (error as any)?.code,
              }
            : { message: String(error) };
        console.error("[updateItemStatusesDb] update failed", {
          itemId: sanitized,
          error: errorInfo,
        });
        failures.push({ itemId: sanitized, error });
      }
    }

    if (failures.length === 0) {
      return { ok: true };
    }

    const primaryError = failures[0]?.error;
    const reason = inferUpdateFailureReason(primaryError);
    console.warn("[updateItemStatusesDb] update failures detected", {
      reason,
      failedItems: failures.map((entry) => entry.itemId),
    });
    if (reason === "unauthorized") {
      const currentUser = auth.currentUser;
      console.warn("[updateItemStatusesDb] unauthorized failure with auth snapshot", {
        hasCurrentUser: Boolean(currentUser),
        uid: currentUser?.uid,
        navigatorOnline:
          typeof navigator !== "undefined" ? navigator.onLine : undefined,
      });
    }

    return {
      ok: false,
      reason,
      failedItems: failures.map((entry) => entry.itemId),
      error: primaryError,
    };
};

const inferUpdateFailureReason = (
  error: unknown
): "unauthorized" | "network" | "unknown" => {
  if (!error || typeof error !== "object") {
    return "unknown";
  }
  const candidate = error as Record<string, unknown>;
  const code =
    typeof candidate.code === "string" ? candidate.code : undefined;
  if (code === "permission-denied" || code === "unauthenticated") {
    return "unauthorized";
  }
  if (code === "unavailable" || code === "deadline-exceeded") {
    return "network";
  }
  const message =
    typeof candidate.message === "string"
      ? candidate.message.toLowerCase()
      : "";
  if (message.includes("network") || message.includes("offline")) {
    return "network";
  }
  return "unknown";
};

const extractErrorDetails = (
  error: unknown
): { code?: string; message?: string } => {
  if (!error) return {};
  if (typeof error === "string") {
    return { message: error };
  }
  if (typeof error === "object") {
    const candidate = error as Record<string, unknown>;
    const code =
      typeof candidate.code === "string" ? candidate.code : undefined;
    const message =
      typeof candidate.message === "string" ? candidate.message : undefined;
    return { code, message };
  }
  return { message: String(error) };
};

const isNetworkAuthError = (error: unknown): boolean => {
  const details = extractErrorDetails(error);
  const code = details.code?.toLowerCase() ?? "";
  const message = details.message?.toLowerCase() ?? "";
  return (
    code.includes("network") ||
    code.includes("unavailable") ||
    message.includes("network") ||
    message.includes("offline")
  );
};

const resolveDropoffPayload = (
  payload: DropoffLocationPayload | undefined,
  itemId: string
): ResolvedDropoffUpdate | undefined => {
  if (!payload) return undefined;
  const { latitude, longitude } = payload;
  if (
    typeof latitude !== "number" ||
    Number.isNaN(latitude) ||
    typeof longitude !== "number" ||
    Number.isNaN(longitude)
  ) {
    console.warn("[updateItemStatuses] dropoff payload missing coords", {
      itemId,
      latitude,
      longitude,
    });
    return undefined;
  }
  try {
    const geoPoint = new GeoPoint(latitude, longitude);
    const accuracy =
      typeof payload.accuracyMeters === "number" &&
      !Number.isNaN(payload.accuracyMeters)
        ? payload.accuracyMeters
        : undefined;
    let capturedAt: Timestamp | undefined;
    if (
      payload.capturedAt instanceof Date &&
      !Number.isNaN(payload.capturedAt.getTime())
    ) {
      capturedAt = Timestamp.fromDate(payload.capturedAt);
    } else {
      capturedAt = Timestamp.now();
    }
    const result: ResolvedDropoffUpdate = {
      geoPoint,
      source: payload.source,
      capturedAt,
    };
    if (typeof accuracy === "number") {
      result.accuracyMeters = accuracy;
    }
    return result;
  } catch (error) {
    console.warn(
      "[updateItemStatuses] failed to construct GeoPoint for dropoff",
      {
        itemId,
        error,
      }
    );
    return undefined;
  }
};

const normalizeDropoffForQueue = (
  payload: DropoffLocationPayload | undefined
): QueueDropoffPayload | undefined => {
  if (!payload) return undefined;
  const { latitude, longitude, accuracyMeters, source, capturedAt } = payload;
  let isoCaptured: string | undefined;
  if (capturedAt instanceof Date) {
    isoCaptured = capturedAt.toISOString();
  } else if (typeof capturedAt === "string" && capturedAt.length) {
    const parsed = new Date(capturedAt);
    if (!Number.isNaN(parsed.getTime())) {
      isoCaptured = parsed.toISOString();
    }
  }
  return {
    latitude,
    longitude,
    accuracyMeters,
    source,
    capturedAt: isoCaptured,
  };
};

const addQueueTagToSource = (
  source: string | undefined,
  tag: string | undefined
): string | undefined => {
  if (!tag) return source;
  const trimmedSource = source?.trim();
  if (!trimmedSource || trimmedSource.length === 0) {
    return tag === "offline" ? "offline-queued" : tag;
  }
  if (trimmedSource.toLowerCase().includes(tag.toLowerCase())) {
    return trimmedSource;
  }
  return `${trimmedSource} (${tag})`;
};

const toQueuePayload = (
  update: UpdateObject,
  options: { tag?: string } = {}
): ItemStatusMutationPayload => {
  const dropoff = normalizeDropoffForQueue(update.dropoff);
  const tag = options.tag;
  return {
    entity: "item",
    itemId: update.itemId,
    orderId: update.orderId,
    shipmentIndex: update.shipmentIndex,
    newStatus: update.newStatus,
    newProcessedState: update.newProcessedState,
    dropoff: dropoff
      ? {
          ...dropoff,
          source: addQueueTagToSource(dropoff.source, tag),
        }
      : undefined,
    updateMethod: update.updateMethod,
    prevStatus: update.prevStatus,
  };
};

const queuePayloadToUpdateObject = (
  payload: ItemStatusMutationPayload
): UpdateObject => ({
  itemId: payload.itemId,
  orderId: payload.orderId,
  newStatus: payload.newStatus,
  shipmentIndex: payload.shipmentIndex,
  newProcessedState: payload.newProcessedState,
  dropoff: payload.dropoff
    ? {
        latitude: payload.dropoff.latitude,
        longitude: payload.dropoff.longitude,
        accuracyMeters: payload.dropoff.accuracyMeters,
        source: payload.dropoff.source,
        capturedAt:
          payload.dropoff.capturedAt instanceof Date
            ? payload.dropoff.capturedAt
            : payload.dropoff.capturedAt
            ? new Date(payload.dropoff.capturedAt)
            : undefined,
      }
    : undefined,
  dropoffResolved: undefined,
  updateMethod: payload.updateMethod,
  prevStatus: payload.prevStatus,
});

const buildDbUpdateFromQueuePayload = (
  payload: ItemStatusMutationPayload
): DBUpdateObject => {
  const update = queuePayloadToUpdateObject(payload);
  const resolvedDrop = resolveDropoffPayload(update.dropoff, update.itemId);
  let shipmentId: string | undefined;
  try {
    const idx = typeof update.shipmentIndex === 'number' ? update.shipmentIndex : -1;
    shipmentId = shipmentsRef.current?.[idx]?.id;
  } catch {}
  return {
    itemId: update.itemId,
    newStatus: update.newStatus,
    newProcessedState: update.newProcessedState,
    dropoff: resolvedDrop,
    updateMethod: update.updateMethod,
    scanHistoryEntry: {
      at: Timestamp.now(),
      statusBeforeScan: update.prevStatus,
      statusAfterScan: update.newStatus,
      byUserId: authUser?.uid,
      shipmentId,
      method: update.updateMethod,
      ...(resolvedDrop
        ? {
            geo: resolvedDrop.geoPoint,
            geoAccuracyMeters: resolvedDrop.accuracyMeters,
            geoSource: resolvedDrop.source,
          }
        : {}),
    },
  };
};

  const updateItemStatusesStateForShipments = (
    updates: UpdateObject[],
    shipmentsState: Shipment[],
    setShipmentsState: (s: Shipment[]) => void
  ) => {
    console.log("[updateItemStatusesStateForShipments] enter", {
      updatesCount: updates?.length ?? 0,
    });
    if (!updates || updates.length === 0) {
      console.log(
        "[updateItemStatusesStateForShipments] no updates provided - exit"
      );
      return false;
    }

    // Deep-ish clone shipments array and top-level ordersSnapshot maps to avoid mutating state directly
    const newShipments: Shipment[] = shipmentsState.map((s) => ({
      ...s,
      ordersSnapshot: s.ordersSnapshot ? { ...s.ordersSnapshot } : {},
    }));
    console.log("[updateItemStatusesStateForShipments] cloned shipments", {
      originalCount: shipmentsState.length,
      clonedCount: newShipments.length,
    });

    let anyFound = false;
    const processedItems: {
      shipmentIndex: number;
      orderId: string;
      itemId: string;
      before?: any;
      after?: any;
    }[] = [];

    for (const upd of updates) {
      const { itemId, orderId, newStatus, shipmentIndex, newProcessedState } =
        upd;
      console.log("[updateItemStatusesStateForShipments] processing update", {
        itemId,
        orderId,
        shipmentIndex,
        newStatus,
        newProcessedState,
      });

      if (
        typeof shipmentIndex !== "number" ||
        shipmentIndex < 0 ||
        shipmentIndex >= newShipments.length
      ) {
        console.warn(
          "[updateItemStatusesStateForShipments] shipmentIndex out of range or missing - skip",
          { shipmentIndex, itemId, orderId }
        );
        continue;
      }

      const shipment = newShipments[shipmentIndex];
      if (!shipment) {
        console.warn(
          "[updateItemStatusesStateForShipments] shipment not found at index - skip",
          { shipmentIndex, itemId, orderId }
        );
        continue;
      }

      if (
        !shipment.ordersSnapshot ||
        typeof shipment.ordersSnapshot !== "object"
      ) {
        console.warn(
          "[updateItemStatusesStateForShipments] shipment.ordersSnapshot missing or invalid - skip",
          { shipmentIndex, itemId, orderId, shipmentPath: shipment.id ?? null }
        );
        continue;
      }

      const orderObj = shipment.ordersSnapshot[orderId];
      if (!orderObj) {
        console.warn(
          "[updateItemStatusesStateForShipments] orderId not present in shipment.ordersSnapshot - skip",
          { shipmentIndex, orderId, itemId }
        );
        continue;
      }

      const includedMap =
        orderObj.includedItemsSnapshot &&
        typeof orderObj.includedItemsSnapshot === "object"
          ? { ...orderObj.includedItemsSnapshot }
          : {};

      const existingItem = includedMap[itemId];
      if (!existingItem) {
        console.warn(
          "[updateItemStatusesStateForShipments] item not found in includedItemsSnapshot - skip",
          { shipmentIndex, orderId, itemId }
        );
        continue;
      }

      // record before state for logging
      const beforeItem = { ...existingItem };
      const afterItem = { ...beforeItem, status: newStatus };
      if (typeof newProcessedState !== "undefined")
        afterItem.currentProcessed = newProcessedState;
      if (upd.dropoffResolved) {
        afterItem.dropoffGeo = upd.dropoffResolved.geoPoint;
        afterItem.dropoffGeoSource = upd.dropoffResolved.source;
        afterItem.dropoffGeoCapturedAt = upd.dropoffResolved.capturedAt;
        if (typeof upd.dropoffResolved.accuracyMeters === "number") {
          afterItem.dropoffGeoAccuracyMeters =
            upd.dropoffResolved.accuracyMeters;
        } else {
          delete (afterItem as any).dropoffGeoAccuracyMeters;
        }
      }

      // set updated item into cloned included map
      includedMap[itemId] = afterItem;

      // clone order object and assign updated includedItemsSnapshot
      const orderAggregates = computeDestinationsAggregate(includedMap);
      const newOrderObj = {
        ...orderObj,
        includedItemsSnapshot: includedMap,
        destinations: orderAggregates.destinations,
        destinationsGeo: orderAggregates.destinationsGeo,
      };
      // ensure shipment.ordersSnapshot is cloned (already shallow-cloned above) and set the new order object
      shipment.ordersSnapshot = {
        ...shipment.ordersSnapshot,
        [orderId]: newOrderObj,
      };
      const shipmentAggregates = computeDestinationsAggregate(
        collectShipmentItems(shipment.ordersSnapshot)
      );
      shipment.destinations = shipmentAggregates.destinations;
      shipment.destinationsGeo = shipmentAggregates.destinationsGeo;

      anyFound = true;
      processedItems.push({
        shipmentIndex,
        orderId,
        itemId,
        before: beforeItem,
        after: afterItem,
      });

      console.log("[updateItemStatusesStateForShipments] applied update", {
        shipmentIndex,
        shipmentId: shipment.id ?? null,
        orderId,
        itemId,
        before_status: beforeItem?.status,
        before_processed: beforeItem?.currentProcessed,
        before_destination: beforeItem?.destination,
        after_status: afterItem?.status,
        after_processed: afterItem?.currentProcessed,
        after_destination: afterItem?.destination,
      });
    }

    if (anyFound) {
      console.log(
        "[updateItemStatusesStateForShipments] committing updated shipments to React state",
        {
          processedCount: processedItems.length,
          sampleProcessed: processedItems.slice(0, 5),
        }
      );
      setShipmentsState(newShipments);
      return true;
    }

    console.log(
      "[updateItemStatusesStateForShipments] no matching items were found in local state - nothing committed"
    );
    return false;
  };

  const updateItemStatusesStateForOrders = (
    updates: UpdateObject[],
    ordersState: Order[],
    setOrdersState: (o: Order[]) => void
  ) => {
    console.log("[updateItemStatusesStateForOrders] enter", {
      updatesCount: updates?.length ?? 0,
    });
    if (!updates || updates.length === 0) {
      console.log(
        "[updateItemStatusesStateForOrders] no updates provided - exit"
      );
      return false;
    }

    const newOrders: Order[] = ordersState.map((o) => ({
      ...o,
      itemsSnapshot: o.itemsSnapshot ? { ...o.itemsSnapshot } : undefined,
    }));
    console.log("[updateItemStatusesStateForOrders] cloned orders", {
      originalCount: ordersState.length,
      clonedCount: newOrders.length,
    });

    let anyFound = false;
    const processedItems: {
      orderIndex: number;
      orderId: string;
      itemId: string;
      before?: any;
      after?: any;
    }[] = [];

    for (const upd of updates) {
      const { itemId, orderId, newStatus, newProcessedState } = upd;
      const orderIndex = newOrders.findIndex((o) => o.id === orderId);
      console.log("[updateItemStatusesStateForOrders] processing update", {
        itemId,
        orderId,
        orderIndex,
        newStatus,
        newProcessedState,
      });

      if (orderIndex === -1) {
        console.warn(
          "[updateItemStatusesStateForOrders] order not found in orders state - skip",
          { orderId, itemId }
        );
        continue;
      }

      const order = newOrders[orderIndex];
      if (!order) {
        console.warn(
          "[updateItemStatusesStateForOrders] order undefined after lookup - skip",
          { orderId, itemId }
        );
        continue;
      }

      const itemsMap =
        order.itemsSnapshot && typeof order.itemsSnapshot === "object"
          ? { ...order.itemsSnapshot }
          : {};

      const existingItem = itemsMap[itemId];
      if (!existingItem) {
        console.warn(
          "[updateItemStatusesStateForOrders] item not found in order.itemsSnapshot - skip",
          { orderId, itemId }
        );
        continue;
      }

      const beforeItem = { ...existingItem };
      const afterItem = { ...beforeItem, status: newStatus };
      if (typeof newProcessedState !== "undefined")
        afterItem.currentProcessed = newProcessedState;
      if (upd.dropoffResolved) {
        afterItem.dropoffGeo = upd.dropoffResolved.geoPoint;
        afterItem.dropoffGeoSource = upd.dropoffResolved.source;
        afterItem.dropoffGeoCapturedAt = upd.dropoffResolved.capturedAt;
        if (typeof upd.dropoffResolved.accuracyMeters === "number") {
          afterItem.dropoffGeoAccuracyMeters =
            upd.dropoffResolved.accuracyMeters;
        } else {
          delete (afterItem as any).dropoffGeoAccuracyMeters;
        }
      }

      itemsMap[itemId] = afterItem;

      const orderAggregates = computeDestinationsAggregate(itemsMap);
      const newOrder = {
        ...order,
        itemsSnapshot: itemsMap,
        destinations: orderAggregates.destinations,
        destinationsGeo: orderAggregates.destinationsGeo,
      };
      newOrders[orderIndex] = newOrder;

      anyFound = true;
      processedItems.push({
        orderIndex,
        orderId,
        itemId,
        before: beforeItem,
        after: afterItem,
      });

      console.log(
        "[updateItemStatusesStateForOrders] applied update to cloned order state",
        {
          orderIndex,
          orderId,
          itemId,
          before: beforeItem,
          after: afterItem,
        }
      );
    }

    if (anyFound) {
      console.log(
        "[updateItemStatusesStateForOrders] committing updated orders to React state",
        {
          processedCount: processedItems.length,
          sampleProcessed: processedItems.slice(0, 5),
        }
      );
      setOrdersState(newOrders);
      return true;
    }

    console.log(
      "[updateItemStatusesStateForOrders] no matching items were found in local orders state - nothing committed"
    );
    return false;
  };

  const updateItemStatus = async (update: UpdateObject) => {
    const ok = await updateItemStatuses([update]);
    return ok;
  };

  const applyUpdatesToState = useCallback((pendingUpdates: UpdateObject[]) => {
    if (!pendingUpdates || pendingUpdates.length === 0) return;
    if (userData?.role === "manufacturer" || userData?.role === "driver") {
      updateItemStatusesStateForShipments(pendingUpdates, shipmentsRef.current, setShipments);
    } else if (userData?.role === "dealer") {
      updateItemStatusesStateForOrders(pendingUpdates, ordersRef.current, setOrders);
    } else {
      updateItemStatusesStateForShipments(pendingUpdates, shipmentsRef.current, setShipments);
    }
  }, [userData?.role]);

  const updateItemStatuses = async (updates: UpdateObject[]) => {
    if (!updates || updates.length === 0) return false;

    const dbUpdates: DBUpdateObject[] = updates.map((u) => {
      const resolved = resolveDropoffPayload(u.dropoff, u.itemId);
      u.dropoffResolved = resolved;
      let shipmentId: string | undefined;
      try {
        const idx = typeof u.shipmentIndex === 'number' ? u.shipmentIndex : -1;
        shipmentId = shipmentsRef.current?.[idx]?.id;
      } catch {}
      return {
        itemId: u.itemId,
        newStatus: u.newStatus,
        newProcessedState: u.newProcessedState,
        dropoff: resolved,
        updateMethod: u.updateMethod,
        // pass through lightweight scan history context
        scanHistoryEntry: {
          at: Timestamp.now(),
          statusBeforeScan: u.prevStatus,
          statusAfterScan: u.newStatus,
          byUserId: authUser?.uid,
          shipmentId,
          method: u.updateMethod,
          ...(resolved
            ? {
                geo: resolved.geoPoint,
                geoAccuracyMeters: resolved.accuracyMeters,
                geoSource: resolved.source,
              }
            : {}),
        },
      };
    });

    const getFailedIds = (result: UpdateDbResult) =>
      result.ok ? new Set<string>() : new Set(result.failedItems);
    const getFailedUpdates = (ids: Set<string>) =>
      updates.filter((u) => ids.has(u.itemId));
    const getFailedDbUpdates = (ids: Set<string>) =>
      dbUpdates.filter((u) => ids.has(u.itemId));
    const getSuccessfulUpdates = (ids: Set<string>) =>
      updates.filter((u) => !ids.has(u.itemId));
    const getSampleUpdate = (items: UpdateObject[]) => items[0];

    const logUnauthorizedFailure = (
      context: string,
      result: UpdateDbResult,
      failedUpdates: UpdateObject[]
    ) => {
      if (result.ok) return;
      const details = extractErrorDetails(result.error);
      const sample = getSampleUpdate(failedUpdates);
      logSyncIssue({
        event: "update-item-status",
        reason: "unauthorized",
        context,
        errorCode: details.code,
        errorMessage: details.message,
        itemId: sample?.itemId,
        orderId: sample?.orderId,
        updateMethod: sample?.updateMethod,
      });
    };

    const refreshAuthAndRetry = async (
      context: string,
      failedDbUpdates: DBUpdateObject[],
      failedUpdates: UpdateObject[]
    ): Promise<UpdateDbResult | null> => {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        const sample = getSampleUpdate(failedUpdates);
        logSyncIssue({
          event: "update-item-status",
          reason: "unauthenticated",
          context,
          itemId: sample?.itemId,
          orderId: sample?.orderId,
          updateMethod: sample?.updateMethod,
        });
        return null;
      }
      try {
        await currentUser.getIdToken(true);
        await currentUser.reload();
      } catch (error) {
        const details = extractErrorDetails(error);
        const sample = getSampleUpdate(failedUpdates);
        logSyncIssue({
          event: "auth-refresh",
          reason: "token-refresh-failed",
          context,
          errorCode: details.code,
          errorMessage: details.message,
          itemId: sample?.itemId,
          orderId: sample?.orderId,
          updateMethod: sample?.updateMethod,
        });
        return null;
      }

      const retryResult = await updateItemStatusesDb(failedDbUpdates);
      if (!retryResult.ok && retryResult.reason === "unauthorized") {
        logUnauthorizedFailure(`${context}-retry`, retryResult, failedUpdates);
      }
      return retryResult;
    };

    if (!featureEnabled || !queueManager) {
      const directResult = await updateItemStatusesDb(dbUpdates);
      console.log("[updateItemStatuses] direct db result", directResult);
      if (directResult.ok) {
        applyUpdatesToState(updates);
        return true;
      }

      const failedIds = getFailedIds(directResult);
      const failedUpdates = getFailedUpdates(failedIds);
      const successfulUpdates = getSuccessfulUpdates(failedIds);

      if (directResult.reason === "unauthorized") {
        if (failedUpdates.length > 0) {
          const retryResult = await refreshAuthAndRetry(
            "direct",
            getFailedDbUpdates(failedIds),
            failedUpdates
          );
          if (retryResult?.ok) {
            applyUpdatesToState(updates);
            return true;
          }
        }
        if (successfulUpdates.length > 0) {
          applyUpdatesToState(successfulUpdates);
        }
        return false;
      }

      return false;
    }

    const shouldQueue =
      !isOnline || isForcedOffline || Boolean(queueState?.paused);

    if (shouldQueue) {
      const tag = !isOnline || isForcedOffline ? "offline" : undefined;
      for (const update of updates) {
        await enqueueItemStatus(toQueuePayload(update, { tag }));
      }
      applyUpdatesToState(updates);
      return true;
    }

    const result = await updateItemStatusesDb(dbUpdates);
    console.log("[updateItemStatuses] db result", result);

    if (result.ok) {
      applyUpdatesToState(updates);
      return true;
    }

    const failedIds = getFailedIds(result);
    const failedUpdates = getFailedUpdates(failedIds);
    const successfulUpdates = getSuccessfulUpdates(failedIds);

    if (failedUpdates.length === 0) {
      return false;
    }

    if (result.reason === "unauthorized") {
      const retryResult = await refreshAuthAndRetry(
        "direct-online",
        getFailedDbUpdates(failedIds),
        failedUpdates
      );
      if (retryResult?.ok) {
        applyUpdatesToState([...successfulUpdates, ...failedUpdates]);
        return true;
      }
      if (retryResult && retryResult.reason === "network") {
        for (const update of failedUpdates) {
          await enqueueItemStatus(toQueuePayload(update, { tag: "offline" }));
        }
        applyUpdatesToState(updates);
        return true;
      }
      if (successfulUpdates.length > 0) {
        applyUpdatesToState(successfulUpdates);
      }
      return false;
    }

    for (const update of failedUpdates) {
      const tag = result.reason === "network" ? "offline" : undefined;
      await enqueueItemStatus(toQueuePayload(update, { tag }));
    }

    applyUpdatesToState(updates);
    return true;
  };


  useEffect(() => {
    if (!featureEnabled || !registerProcessor || !queueManager) return;
    const unsubscribe = registerProcessor("update-item-status", async (item): Promise<OfflineProcessorResult> => {
      if (item.payload.entity !== "item") {
        console.warn("[OfflineSync] Unsupported payload entity", item.payload.entity);
        return { ok: true };
      }
      const dbUpdate = buildDbUpdateFromQueuePayload(item.payload);
      const dbResult = await updateItemStatusesDb([dbUpdate]);

      if (dbResult.ok) {
        const update = queuePayloadToUpdateObject(item.payload);
        update.dropoffResolved = dbUpdate.dropoff;
        applyUpdatesToState([update]);
        return { ok: true, lastSyncAt: Date.now() };
      }

      if (dbResult.reason === "unauthorized") {
        console.warn("[OfflineSync] Unauthorized while syncing item, attempting token refresh", {
          queueItemId: item.id,
          itemId: dbUpdate.itemId,
          hasCurrentUser: Boolean(auth.currentUser),
          currentUid: auth.currentUser?.uid,
        });
        const currentUser = auth.currentUser;
        if (currentUser) {
          try {
            await currentUser.getIdToken(true);
            await currentUser.reload();
            console.log("[OfflineSync] Retrying sync after token refresh", {
              queueItemId: item.id,
              itemId: dbUpdate.itemId,
            });
            const retryResult = await updateItemStatusesDb([dbUpdate]);
            if (retryResult.ok) {
              const update = queuePayloadToUpdateObject(item.payload);
              update.dropoffResolved = dbUpdate.dropoff;
              applyUpdatesToState([update]);
              return { ok: true, lastSyncAt: Date.now() };
            }
            if (retryResult.reason === "network") {
              console.warn("[OfflineSync] Retry after token refresh failed due to network", {
                queueItemId: item.id,
                itemId: dbUpdate.itemId,
              });
              return {
                ok: false,
                retry: true,
                errorMessage: "Network unavailable",
              };
            }
            if (retryResult.reason !== "unauthorized") {
              console.warn("[OfflineSync] Retry after token refresh failed with unexpected reason", {
                queueItemId: item.id,
                itemId: dbUpdate.itemId,
                reason: retryResult.reason,
              });
              return {
                ok: false,
                retry: false,
                errorMessage: "Unexpected sync failure",
              };
            }
            logSyncIssue({
              event: "update-item-status",
              reason: "unauthorized",
              context: "queue-retry",
              itemId: dbUpdate.itemId,
              queueItemId: item.id,
              updateMethod: dbUpdate.updateMethod,
            });
            console.warn("[OfflineSync] Retry after token refresh still unauthorized", {
              queueItemId: item.id,
              itemId: dbUpdate.itemId,
            });
          } catch (refreshError) {
            const details = extractErrorDetails(refreshError);
            logSyncIssue({
              event: "auth-refresh",
              reason: "token-refresh-failed",
              context: "queue",
              errorCode: details.code,
              errorMessage: details.message,
              itemId: dbUpdate.itemId,
              queueItemId: item.id,
              updateMethod: dbUpdate.updateMethod,
            });
            console.error("[OfflineSync] Token refresh failed", {
              queueItemId: item.id,
              itemId: dbUpdate.itemId,
              error: refreshError instanceof Error
                ? { name: refreshError.name, message: refreshError.message }
                : refreshError,
            });
            if (isNetworkAuthError(refreshError)) {
              return {
                ok: false,
                retry: true,
                errorMessage: "Network unavailable",
              };
            }
          }
        } else {
          logSyncIssue({
            event: "update-item-status",
            reason: "unauthenticated",
            context: "queue",
            itemId: dbUpdate.itemId,
            queueItemId: item.id,
            updateMethod: dbUpdate.updateMethod,
          });
          console.warn("[OfflineSync] Unauthorized sync detected but no authenticated user present", {
            queueItemId: item.id,
            itemId: dbUpdate.itemId,
          });
        }
        return {
          ok: false,
          retry: false,
          errorMessage: "Authentication required",
          pause: true,
          pauseReason: "Authentication required",
        };
      }

      if (dbResult.reason === "network") {
        return {
          ok: false,
          retry: true,
          errorMessage: "Network unavailable",
        };
      }

      return {
        ok: false,
        retry: false,
        errorMessage: "Unexpected sync failure",
      };
    });
    return () => {
      unsubscribe();
    };
  }, [
    applyUpdatesToState,
    featureEnabled,
    logSyncIssue,
    queueManager,
    registerProcessor,
    updateItemStatusesDb,
  ]);


  return (
    <PrimaryFetchedDataContext.Provider
      value={{
        updateItemStatuses,
        fetchShipmentsByManufacturerId,
        fetchShipmentsForDriver,
        fetchOrdersByDealerId,
        orders,
        updateItemStatus,
        shipments,
      }}
    >
      {children}
    </PrimaryFetchedDataContext.Provider>
  );
};
